///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import {
	DiscreteGestureRecognizer,
	GestureState,
	type GestureEventData,
	type GestureRecognizerConfig,
	type TouchPoint
} from "./GestureRecognizer"

export interface TapGestureEventData extends GestureEventData {
	// Number of taps (1 for single tap, 2 for double tap, etc.)
	tapCount: number
	// Number of fingers used
	touchCount: number
}

export interface TapGestureConfig extends GestureRecognizerConfig {
	// Number of taps required (default: 1)
	numberOfTapsRequired?: number
	// Maximum movement allowed between tap down and up (in pixels)
	maxMovement?: number
	// Maximum duration for a tap (in ms)
	maxDuration?: number
	// For multi-tap: maximum time between taps (in ms)
	maxIntervalBetweenTaps?: number
}

/**
 * Recognizes tap gestures (discrete)
 *
 * Supports single tap, double tap, triple tap, etc.
 * Configurable number of taps and touches required.
 *
 * ## Handling Tap Events
 *
 * Use `onGesture()` to handle successful taps. Events are ONLY emitted when
 * this recognizer wins the arena conflict resolution:
 *
 * ```typescript
 * tapRecognizer.onGesture((data) => {
 *     // This callback ONLY fires when this tap handler wins
 *     // No need to check state - if you receive an event, you won
 *     console.log("Tap at:", data.x, data.y)
 *     console.log("Hit point:", data.ownerHits[0]?.hitPoint)
 * })
 * ```
 *
 * ## Subclassing
 *
 * For complex handlers that need access to instance state:
 *
 * ```typescript
 * class MyTapHandler extends TapGestureRecognizer {
 *     // Override didBecomeActive to perform actions when this handler wins
 *     public override didBecomeActive(): void {
 *         // Custom logic when tap is recognized AND this handler won
 *         super.didBecomeActive() // This emits the event
 *     }
 * }
 * ```
 */
export class TapGestureRecognizer extends DiscreteGestureRecognizer<TapGestureEventData> {
	private numberOfTapsRequired: number
	private maxMovement: number
	private maxDuration: number
	private maxIntervalBetweenTaps: number

	private tapCount: number = 0
	private lastTapTime: number = 0
	private lastTapLocation?: { x: number, y: number }
	private touchStartTime: number = 0
	private startLocation?: { x: number, y: number }
	private pendingTapTimeout?: ReturnType<typeof setTimeout>

	constructor(config: TapGestureConfig = {}) {
		super(config)
		this.numberOfTapsRequired = config.numberOfTapsRequired ?? 1
		this.maxMovement = config.maxMovement ?? 10
		this.maxDuration = config.maxDuration ?? 300
		this.maxIntervalBetweenTaps = config.maxIntervalBetweenTaps ?? 300
	}

	get gestureType(): string {
		return this.numberOfTapsRequired === 1 ? "tap" : `tap-${this.numberOfTapsRequired}`
	}

	handleTouchStart(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// For multi-tap: clear old touches from previous taps so getOwnerHits()
		// returns the hit point from the current tap, not a stale one
		if (this.numberOfTapsRequired > 1) {
			this.touches.clear()
		}

		// Update tracked touches
		touches.forEach(t => {
			this.touches.set(t.id, t)
		})

		// Check touch count requirements
		if (this.touchCount < this._minimumTouches || this.touchCount > this._maximumTouches) {
			this.fail()
			return
		}

		const centroid = this.getCentroid()
		this.touchStartTime = performance.now()
		this.startLocation = centroid

		// Clear pending tap if touch count changes
		if (this.pendingTapTimeout) {
			clearTimeout(this.pendingTapTimeout)
			this.pendingTapTimeout = undefined
		}
	}

	handleTouchMove(touches: TouchPoint[]): void {
		if (!this._enabled || this.isTerminal()) return

		// Update tracked touches
		touches.forEach(t => {
			const existing = this.touches.get(t.id)
			if (existing) {
				existing.x = t.x
				existing.y = t.y
			}
		})

		// Check if movement exceeds threshold
		if (this.startLocation) {
			const centroid = this.getCentroid()
			const dx = centroid.x - this.startLocation.x
			const dy = centroid.y - this.startLocation.y
			const distance = Math.sqrt(dx * dx + dy * dy)

			if (distance > this.maxMovement) {
				this.fail()
			}
		}
	}

	handleTouchEnd(touches: TouchPoint[]): void {
		if (!this._enabled || this.isTerminal()) return

		// Save the ending touches for hit data (needed by createEventData)
		// Don't delete yet - we need them for getOwnerHits()
		const endingTouches = touches.filter(t => this.touches.has(t.id))

		// Check if all touches will be released after this
		const remainingCount = this.touchCount - endingTouches.length
		if (remainingCount > 0) {
			// Remove the ended touches and wait for more
			touches.forEach(t => this.touches.delete(t.id))
			return
		}

		// All touches released - keep them in this.touches until after recognition
		// so that getOwnerHits() can access the gesture hit data

		const now = performance.now()
		const duration = now - this.touchStartTime

		// Check if tap was too long
		if (duration > this.maxDuration) {
			this.fail()
			return
		}

		// Check if this tap is part of a multi-tap sequence
		const timeSinceLastTap = now - this.lastTapTime

		if (this.numberOfTapsRequired > 1) {
			// Multi-tap logic

			// Clear any pending timeout from previous tap
			if (this.pendingTapTimeout) {
				clearTimeout(this.pendingTapTimeout)
				this.pendingTapTimeout = undefined
			}

			if (this.lastTapLocation && timeSinceLastTap < this.maxIntervalBetweenTaps) {
				// Check if tap is near the previous tap
				const centroid = this.startLocation!
				const dx = centroid.x - this.lastTapLocation.x
				const dy = centroid.y - this.lastTapLocation.y
				const distance = Math.sqrt(dx * dx + dy * dy)

				if (distance < this.maxMovement * 2) {
					this.tapCount++
				} else {
					this.tapCount = 1
				}
			} else {
				this.tapCount = 1
			}

			this.lastTapTime = now
			this.lastTapLocation = this.startLocation

			if (this.tapCount >= this.numberOfTapsRequired) {
				this.recognize()
				this.tapCount = 0
				this.lastTapLocation = undefined
			} else {
				// Wait for potential additional taps
				this.pendingTapTimeout = setTimeout(() => {
					this.fail()
					this.tapCount = 0
					this.lastTapLocation = undefined
				}, this.maxIntervalBetweenTaps)
			}
		} else {
			// Single tap
			this.tapCount = 1

			// If we have requireToFail recognizers that haven't resolved yet, defer recognition
			if (this._requireToFail.length > 0 && !this.canRecognize()) {
				// Mark as deferred so recognize() knows to emit directly
				this.markDeferred()
				// Wait for requireToFail recognizers to fail, then recognize.
				// The wait must be based on the dependencies' tap intervals, not
				// just our own: a double-tap configured with a longer interval
				// than ours would otherwise still be Possible when this timer
				// fires, silently dropping the single tap. Add a 50ms buffer so
				// their fail timers always resolve first.
				const waitForFailMs = this._requireToFail.reduce((maxMs, r) => {
					return r instanceof TapGestureRecognizer
						? Math.max(maxMs, r.maxIntervalBetweenTaps)
						: maxMs
				}, this.maxIntervalBetweenTaps) + 50
				this.pendingTapTimeout = setTimeout(() => {
					if (this.canRecognize()) {
						this.recognize()
					} else {
						this.fail()
					}
				}, waitForFailMs)
			} else {
				// No requireToFail or they've already failed - recognize immediately
				this.recognize()
			}
		}
	}

	handleTouchCancel(): void {
		if (this.pendingTapTimeout) {
			clearTimeout(this.pendingTapTimeout)
			this.pendingTapTimeout = undefined
		}
		this.tapCount = 0
		this.lastTapLocation = undefined
		this.cancel()
	}

	protected createEventData(state: GestureState): TapGestureEventData {
		const location = this.startLocation ?? { x: 0, y: 0 }
		const ownerHits = this.getOwnerHits()
		return {
			type: "tap",
			state,
			timestamp: performance.now(),
			x: location.x,
			y: location.y,
			ownerHits,
			tapCount: this.tapCount,
			touchCount: this._minimumTouches
		}
	}

	override reset(): void {
		// Cancel any pending tap timeout
		if (this.pendingTapTimeout) {
			clearTimeout(this.pendingTapTimeout)
			this.pendingTapTimeout = undefined
		}
		super.reset()
		this.startLocation = undefined
		this.touchStartTime = 0
		this.tapCount = 0
		this.lastTapLocation = undefined
	}
}

